import { createContext } from 'react';
import type { ReactNode } from 'react';

export interface FloatingActionDefinition {
    id: string;
    order: number;
    visible: boolean;
    content: ReactNode;
}

type FloatingActionOwner = symbol;
type FloatingActionEntry = FloatingActionDefinition & { owner: FloatingActionOwner };
type FloatingActionsListener = () => void;

const EMPTY_ACTIONS: readonly FloatingActionDefinition[] = [];

export class FloatingActionsStore {
    private readonly actions = new Map<string, FloatingActionEntry>();
    private readonly listeners = new Set<FloatingActionsListener>();
    private snapshot: readonly FloatingActionDefinition[] = EMPTY_ACTIONS;

    subscribe = (listener: FloatingActionsListener) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };

    getSnapshot = () => this.snapshot;

    getServerSnapshot = () => EMPTY_ACTIONS;

    setAction(action: FloatingActionDefinition, owner: FloatingActionOwner) {
        const current = this.actions.get(action.id);
        if (
            current?.owner === owner
            && current.order === action.order
            && current.visible === action.visible
            && current.content === action.content
        ) {
            return;
        }

        this.actions.set(action.id, { ...action, owner });
        this.publish();
    }

    removeAction(id: string, owner: FloatingActionOwner) {
        if (this.actions.get(id)?.owner !== owner) {
            return;
        }

        this.actions.delete(id);
        this.publish();
    }

    private publish() {
        this.snapshot = Array.from(this.actions.values())
            .filter((action) => action.visible)
            .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id))
            .map((action) => ({
                id: action.id,
                order: action.order,
                visible: action.visible,
                content: action.content,
            }));
        this.listeners.forEach((listener) => listener());
    }
}

export const FloatingActionsContext = createContext<FloatingActionsStore | null>(null);
